import { forwardRef, type HTMLAttributes } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

const statusVariants = cva("h-2 w-2 rounded-full", {
  variants: {
    status: {
      online: "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.5)]",
      offline: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]",
      warning: "bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.4)]",
      degraded: "bg-orange-500 shadow-[0_0_6px_rgba(249,115,22,0.4)]",
    },
  },
  defaultVariants: { status: "offline" },
});

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof statusVariants> {
  status: "online" | "offline" | "warning" | "degraded";
  label?: string;
  pulse?: boolean;
}

const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(({ className, status, label, pulse = false, ...props }, ref) => (
  <span ref={ref} className={cn("inline-flex items-center gap-1.5 text-sm font-medium", className)} {...props}>
    <span className="relative flex h-3 w-3 items-center justify-center">
      {pulse && <span className="absolute inline-flex h-full w-full animate-pulse-dot rounded-full bg-emerald-400 opacity-75" />}
      <span className={cn(statusVariants({ status }), "relative")} />
    </span>
    {label && <span className="text-foreground">{label}</span>}
  </span>
));
StatusBadge.displayName = "StatusBadge";

export { StatusBadge, statusVariants };
